/** Resident boulder waterlines and short flow-following wakes. Geometry is
 * built once from the actual instance triangles and final water/terrain. */
import * as T from 'three';
import {type PlanetSampler,type TerrainSample,rngFromString} from '@hyperlight/shared';
import {rockSection,sectionDistance} from './shoreRockField';
import {RIVER_SURFACE_LIFT_M} from './riverRibbon';
import type {RockPlacement} from '../flora/rockScatter';

const LIFT=RIVER_SURFACE_LIFT_M+.035,STEP=.4;
export const RIVER_ROCK_FOAM_LIMIT=24;
type P={x:number;y:number};
type Surface={point:T.Vector3;wet:boolean;depth:number;speed:number;flow:T.Vector3};
export function buildRiverRockFoam(sampler:PlanetSampler,source:T.InstancedMesh):T.BufferGeometry|null {
  const evidence=source.userData.rockEvidence as RockPlacement[]|undefined;
  if(!evidence?.some(r=>r.waterM!==undefined))return null;
  const R=sampler.def.radius,A=sampler.amplitude,center=new T.Vector3().fromArray([evidence[0].x,evidence[0].y,evidence[0].z]);
  const positions:number[]=[],uvs:number[]=[],edges:number[]=[],strengths:number[]=[],rates:number[]=[],depths:number[]=[],kinds:number[]=[],indices:number[]=[];
  const records:object[]=[],sample={}as TerrainSample,matrix=new T.Matrix4(),vertex=new T.Vector3();
  let probes=0,accepted=0;
  for(let i=0;i<evidence.length&&accepted<RIVER_ROCK_FOAM_LIMIT;i++){
    const rock=evidence[i];if(rock.waterM===undefined)continue;
    const base=new T.Vector3(rock.x,rock.y,rock.z),up=base.clone().normalize();
    sampler.sample(up.x,up.y,up.z,sample,false);probes++;
    if((sample.riverWs??-1e9)<-1e8||sample.road<0||sample.town>.002||(sample.lavaDepthM??0)>0||(sample.glacierIceM??0)>.02)continue;
    const flow=new T.Vector3(sample.flowx,sample.flowy,sample.flowz).addScaledVector(up,-(sample.flowx*up.x+sample.flowy*up.y+sample.flowz*up.z)).normalize();
    if(flow.lengthSq()<.5)continue;
    const cross=new T.Vector3().crossVectors(flow,up).normalize(),speed=Math.max(.4,Math.min(4,sample.riverSpeed??1));
    const phase=rngFromString(`${sampler.def.seed}:rock-wake:${rock.fallId}:${rock.group}:${rock.role}`)()*60;
    const rate=Math.max(.8,Math.round(speed*.9/.8)*.8); // whole 480-cell cycles / 600 seconds
    const at=(p:P):Surface=>{
      const d=base.clone().addScaledVector(cross,p.x).addScaledVector(flow,p.y).normalize();sampler.sample(d.x,d.y,d.z,sample,false);probes++;
      const ws=sample.riverWs??-1e9,depth=(ws-sample.h)*A;
      const wet=ws>-1e8&&depth>.025&&sample.road>=0&&sample.town<=.002&&(sample.glacierIceM??0)<.02&&(sample.lavaDepthM??0)<=0;
      return{point:d.multiplyScalar(R+(ws>-1e8?ws*A:rock.waterM!)+LIFT),wet,depth:wet?depth:0,speed:sample.riverSpeed??speed,flow:new T.Vector3(sample.flowx,sample.flowy,sample.flowz)};
    };
    // h is signed distance to the local river surface, not one horizontal
    // ocean plane. Triangle sections follow the actual transformed boulder.
    source.getMatrixAt(i,matrix);const pos=source.geometry.getAttribute('position'),ix=source.geometry.index,vertices:{x:number;y:number;h:number}[]=[];
    for(let j=0;j<(ix?.count??pos.count);j++){
      vertex.fromBufferAttribute(pos,ix?ix.getX(j):j).applyMatrix4(matrix);const d=vertex.clone().normalize();sampler.sample(d.x,d.y,d.z,sample,false);probes++;
      const delta=vertex.clone().sub(base),ws=(sample.riverWs??-1e9)>-1e8?sample.riverWs!*A:rock.waterM!;
      vertices.push({x:delta.dot(cross),y:delta.dot(flow),h:vertex.length()-R-ws-LIFT});
    }
    const section=rockSection(vertices,0);if(section.length<3)continue;
    const start=indices.length,local:T.Vector3[]=[],valid:boolean[]=[],coords:P[]=[];
    const add=(p:P,edge:number,strength:number,kind:number)=>{
      const s=at(p),id=positions.length/3;positions.push(...s.point.clone().sub(center).toArray());uvs.push(p.x+phase,p.y);edges.push(edge);strengths.push(strength);rates.push(rate);depths.push(s.depth);kinds.push(kind);
      local[id]=s.point;valid[id]=s.wet;coords[id]=p;return id;
    };
    const keep=(a:number,b:number,c:number)=>{
      if(!valid[a]||!valid[b]||!valid[c])return;
      // Check interiors, not only vertices: a narrow shelf cannot acquire a
      // triangle of hovering foam. Also avoid the rock's submerged interior.
      for(const ids of [[a,b],[b,c],[c,a],[a,b,c]]){
        const p={x:ids.reduce((n,i)=>n+coords[i].x,0)/ids.length,y:ids.reduce((n,i)=>n+coords[i].y,0)/ids.length};
        const s=at(p);if(!s.wet||sectionDistance(p,section)<-.025)return;
        const mid=new T.Vector3();for(const i of ids)mid.add(local[i]);mid.multiplyScalar(1/ids.length);
        if(Math.abs(mid.length()-s.point.length())>.035)return;
      }
      const normal=local[b].clone().sub(local[a]).cross(local[c].clone().sub(local[a]));
      if(normal.dot(up)<0)indices.push(a,c,b);else indices.push(a,b,c);
    };
    const quad=(a:number,b:number,c:number,d:number)=>{keep(a,b,c);keep(b,d,c);};
    // Offset adjacent face normals at each corner. Clamped miters preserve
    // the rock's facets without spikes or circular proxy rings.
    const normals=section.map((p,j)=>{const q=section[(j+1)%section.length],dx=q.x-p.x,dy=q.y-p.y,l=Math.hypot(dx,dy);return{x:dy/l,y:-dx/l};});
    const offsets=section.map((_,j)=>{const a=normals[(j+section.length-1)%section.length],b=normals[j],d=Math.max(.4,1+a.x*b.x+a.y*b.y);return{x:(a.x+b.x)/d,y:(a.y+b.y)/d};});
    let distance=0;
    for(let j=0;j<section.length;j++){
      const k=(j+1)%section.length,a=section[j],b=section[k],length=Math.hypot(b.x-a.x,b.y-a.y),steps=Math.max(1,Math.ceil(length/STEP));
      const facing=Math.max(0,-normals[j].y),strength=.28+.64*facing,width=.16+.19*facing;
      let previous:number[]|null=null;
      for(let n=0;n<=steps;n++){
        const t=n/steps,p={x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t},normal={x:offsets[j].x+(offsets[k].x-offsets[j].x)*t,y:offsets[j].y+(offsets[k].y-offsets[j].y)*t};
        const row=[0,.5,1].map(e=>add({x:p.x+normal.x*(.025+e*width),y:p.y+normal.y*(.025+e*width)},e,strength,0));
        if(previous)for(let c=0;c<2;c++)quad(previous[c],previous[c+1],row[c],row[c+1]);previous=row;
      }
      distance+=length;
    }
    // Two short trails depart from the actual lateral silhouette. Their
    // centerlines follow sampled local flow, and both fade before ending.
    for(const sign of [-1,1]){
      const tip=section.reduce((best,p)=>p.x*sign>best.x*sign?p:best,section[0]);
      let p={x:tip.x+sign*.16,y:tip.y},previous:number[]|null=null;
      const length=Math.min(7.2,2.8+rock.a*.8+speed*.35),rows=Math.ceil(length/STEP);
      for(let row=0;row<=rows;row++){
        const t=row/rows,spread=.17+.42*Math.sin(Math.PI*t),strength=.40*(1-t)*(1-t);
        const ids=[-1,0,1].map(e=>add({x:p.x+e*spread,y:p.y},(e+1)*.5,strength,1));
        if(previous)for(let c=0;c<2;c++)quad(previous[c],previous[c+1],ids[c],ids[c+1]);previous=ids;
        const sample=at(p),velocity=sample.flow.lengthSq()>.01?sample.flow.normalize():flow;
        const fx=velocity.dot(cross),fy=Math.max(.3,velocity.dot(flow));
        p={x:p.x+fx*STEP+sign*.075*STEP,y:p.y+fy*STEP};
      }
    }
    if(indices.length>start){accepted++;records.push({fallId:rock.fallId,group:rock.group,role:rock.role,section,perimeter:distance,triangles:(indices.length-start)/3,speed,rate});}
  }
  if(!indices.length)return null;
  // Rejected dry-bank vertices must not consume GPU storage or enlarge the
  // culling bounds. Compact only after all the surface checks have finished.
  const used=[...new Set(indices)],remap=new Map(used.map((old,i)=>[old,i]));
  const geo=new T.BufferGeometry();
  const attributes:{[key:string]:[number[],number]}={position:[positions,3],uv:[uvs,2],aEdge:[edges,1],aStrength:[strengths,1],aRate:[rates,1],aDepth:[depths,1],aKind:[kinds,1]};
  for(const [key,[values,size]] of Object.entries(attributes)){
    const compact=new Float32Array(used.length*size);
    for(let i=0;i<used.length;i++)for(let j=0;j<size;j++)compact[i*size+j]=values[used[i]*size+j];
    geo.setAttribute(key,new T.BufferAttribute(compact,size));
  }
  geo.setIndex(indices.map(i=>remap.get(i)!));geo.computeBoundingSphere();geo.userData={center:center.toArray(),rocks:records,probes,lift:LIFT};return geo;
}

export function makeRiverRockFoamMaterial(water:T.ShaderMaterial):T.ShaderMaterial {
  return new T.ShaderMaterial({transparent:true,depthWrite:false,fog:true,uniforms:{...T.UniformsUtils.clone(T.UniformsLib.fog),uTime:water.uniforms.uTime,uDayLight:water.uniforms.uDayLight,uFoam:water.uniforms.uFoam},
    vertexShader:`
      #include <common>
      #include <logdepthbuf_pars_vertex>
      #include <fog_pars_vertex>
      attribute float aEdge;attribute float aStrength;attribute float aRate;attribute float aDepth;attribute float aKind;
      varying vec2 vUv;varying float vEdge;varying float vStrength;varying float vRate;varying float vDepth;varying float vKind;varying float vFade;
      void main(){vUv=uv;vEdge=aEdge;vStrength=aStrength;vRate=aRate;vDepth=aDepth;vKind=aKind;
        vec4 mvPosition=modelViewMatrix*vec4(position,1.0);vFade=1.0-smoothstep(100.0,190.0,length(mvPosition.xyz));gl_Position=projectionMatrix*mvPosition;
        #include <fog_vertex>
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader:`
      #include <common>
      #include <logdepthbuf_pars_fragment>
      #include <fog_pars_fragment>
      uniform float uTime;uniform float uDayLight;uniform vec3 uFoam;
      varying vec2 vUv;varying float vEdge;varying float vStrength;varying float vRate;varying float vDepth;varying float vKind;varying float vFade;
      float hash(vec2 p){return fract(sin(dot(mod(p,vec2(90.0,480.0)),vec2(127.1,311.7)))*43758.5453);}
      float field(vec2 p){p.x+=p.y*.375;vec2 i=floor(p),f=fract(p);float a=hash(i),b=hash(i+vec2(1,0)),c=hash(i+vec2(0,1)),d=hash(i+1.0);return f.x+f.y<1.0?a+(b-a)*f.x+(c-a)*f.y:d+(c-d)*(1.0-f.x)+(b-d)*(1.0-f.y);}
      void main(){
        #include <logdepthbuf_fragment>
        float f=field(vec2(vUv.x*1.8,vUv.y*1.2-uTime*vRate));
        float broken=smoothstep(.34,.67,f),veins=1.0-smoothstep(.025,.12,abs(f-.52));
        float edge=smoothstep(0.0,.15,vEdge)*(1.0-smoothstep(.45,1.0,vEdge));
        float coverage=mix(.22+broken*.66,veins*.7+broken*.18,vKind);
        float alpha=edge*coverage*vStrength*vFade*smoothstep(.025,.18,vDepth);
        if(alpha<.008)discard;
        gl_FragColor=vec4(uFoam*(.14+.86*uDayLight),alpha);
        #include <fog_fragment>
      }`});
}

/** One shared material and at most one extra draw per resident rock tile. */
export class RiverRockFoam {
  readonly group=new T.Group();
  readonly material:T.ShaderMaterial;
  readonly entries=new Map<T.InstancedMesh,T.Mesh>();
  constructor(private readonly sampler:PlanetSampler,water:T.ShaderMaterial){this.group.name='river-rock-foam';this.material=makeRiverRockFoamMaterial(water);}
  add(source:T.InstancedMesh):void{if(this.entries.has(source))return;const geo=buildRiverRockFoam(this.sampler,source);if(!geo)return;const mesh=new T.Mesh(geo,this.material);mesh.position.fromArray(geo.userData.center);mesh.renderOrder=5;mesh.visible=source.visible;mesh.name='river-rock-contact-and-wakes';this.entries.set(source,mesh);this.group.add(mesh);}
  delete(source:T.InstancedMesh):void{const mesh=this.entries.get(source);if(!mesh)return;mesh.removeFromParent();mesh.geometry.dispose();this.entries.delete(source);}
  updateVisibility():void{for(const[source,mesh]of this.entries)mesh.visible=source.visible;}
  dispose():void{for(const source of this.entries.keys())this.delete(source);this.material.dispose();this.group.removeFromParent();}
}
